import type { Lang } from './i18n'

/**
 * Las distancias en las unidades de la agencia (hallazgo H-64).
 *
 * Todo se guarda y viaja en metros; lo que cambia es cómo se lee. Una agencia de Texas trabaja en
 * pies y millas, y «a 2000 m» obligaba al operador a convertir en la cabeza en el peor momento.
 * El sistema lo dice la marca de la agencia (Administración › Tema), y por omisión el de su país.
 */
export type UnitSystem = 'metric' | 'imperial'

let sistema: UnitSystem = 'metric'

/** Lo fija la aplicación con la marca de la agencia. Sin marca, métrico. */
export function setUnitSystem(s: string | null | undefined) {
  sistema = s === 'imperial' ? 'imperial' : 'metric'
}

export function unitSystem(): UnitSystem {
  return sistema
}

const PIES_POR_METRO = 3.28084
const METROS_POR_MILLA = 1609.344

function numero(x: number, lang: Lang, decimales: number): string {
  return new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', { maximumFractionDigits: decimales }).format(x)
}

/**
 * Una distancia para leer: «850 m», «2.4 km», «950 ft», «1.5 mi».
 *
 * Los pies se redondean a 10 y los metros a 1: nadie mide una salida de zona con más precisión que
 * el GPS que la detectó. Las millas y los kilómetros llevan un decimal por debajo de 10.
 */
export function fmtDistance(meters: number, lang: Lang, system: UnitSystem = sistema): string {
  if (!Number.isFinite(meters)) return ''
  if (system === 'imperial') {
    const pies = meters * PIES_POR_METRO
    if (pies < 1000) return `${numero(Math.round(pies / 10) * 10, lang, 0)} ft`
    const millas = meters / METROS_POR_MILLA
    return `${numero(millas, lang, millas < 10 ? 1 : 0)} mi`
  }
  if (meters < 1000) return `${numero(Math.round(meters), lang, 0)} m`
  const km = meters / 1000
  return `${numero(km, lang, km < 10 ? 1 : 0)} km`
}

/** Para editar un largo corto (la geocerca): el número en la unidad de la agencia, y de vuelta a metros. */
export const shortLength = {
  unit: (system: UnitSystem = sistema) => (system === 'imperial' ? 'ft' : 'm'),
  fromMeters: (m: number, system: UnitSystem = sistema) => (system === 'imperial' ? Math.round(m * PIES_POR_METRO) : m),
  toMeters: (x: number, system: UnitSystem = sistema) => (system === 'imperial' ? Math.round(x / PIES_POR_METRO) : x),
}
